import { Page, Text, View, Document, Image, Link } from '@react-pdf/renderer';
import { reportStyles } from './styles/ReportStyles';
import React from 'react';
import renderEmergencyMessageContent from '../components/RenderEmergencyMessageContent';
import { FooterText } from '../components/Footer';
import { SafeguardLogo } from '../components/SafeguardLogo';
import { StringKeys } from '../../resources/strings/StringKeys';
import {
  EmergencyMessageOnAzure,
  EmergencyOnAzure,
  UserLocationOnAzure,
} from '../../../generated-client/models';
import { OrgUser } from '@/models/User/OrgUser';
import { TFunction } from 'i18next';

type EmergencyHistoryReportProps = {
  emergency: EmergencyOnAzure;
  emergencyLocations: UserLocationOnAzure[];
  emergencyMessages: EmergencyMessageOnAzure[];
  orgUser: OrgUser | null;
  locationsByUserId: object;
  t: TFunction<'translation', undefined>;
};

export const EmergencyHistoryReport = async ({
  emergency,
  emergencyLocations,
  emergencyMessages,
  orgUser,
  locationsByUserId,
  t,
}: EmergencyHistoryReportProps) => {
  const initiatorLocation = emergencyLocations?.find?.(
    (loc) => loc.user_auth_id.trim() === emergency.creator_auth_id?.trim(),
  );

  const hasLocation = initiatorLocation !== undefined;
  const mapImageUrl = hasLocation
    ? `https://maps.googleapis.com/maps/api/staticmap?center=${initiatorLocation.latitude},${initiatorLocation.longitude}&zoom=15&size=400x200&maptype=roadmap&markers=size:mid%7Ccolor:red%7C${initiatorLocation.latitude},${initiatorLocation.longitude}&key=AIzaSyCCg8Qtosjt5nMuLaRDeu1J0sze0_PXilY`
    : null;

  return (
    <Document>
      <Page size="A4" style={reportStyles.page}>
        {/* Row 1: Logo */}
        <SafeguardLogo />

        {/* Row 2: Incident and Emergency Detail */}
        <View style={reportStyles.section}>
          <Text style={reportStyles.subsectionHeader}>
            {t(StringKeys.EMERGENCY_TYPE)}: {emergency.incident} -{' '}
            {emergency.creator_first_name} {emergency.creator_last_name}
          </Text>
          <Text style={reportStyles.text}>
            <Text style={reportStyles.boldText}>
              {t(StringKeys.EMERGENCY_LOCATION)}:{' '}
            </Text>
            {hasLocation ? (
              <Link
                src={`https://www.google.com/maps?q=${initiatorLocation.latitude},${initiatorLocation.longitude}`}
                style={reportStyles.text}
              >
                {`${Number(initiatorLocation.latitude).toFixed(5)}, ${Number(initiatorLocation.longitude).toFixed(5)}`}
              </Link>
            ) : (
              t(StringKeys.LOCATION_NOT_AVAILABLE)
            )}
          </Text>
          <Text style={reportStyles.text}>
            <Text style={reportStyles.boldText}>
              {t(StringKeys.CREATION_TIME)}:{' '}
            </Text>
            {new Date(emergency.creation_timestamp).toLocaleString()}
          </Text>
          <Text style={reportStyles.text}>
            <Text style={reportStyles.boldText}>
              {t(StringKeys.RESPONSE_TEAM)}:{' '}
            </Text>
            {emergency.users && emergency.users.length > 0
              ? emergency.users.map((user, index, array) => (
                  <React.Fragment key={index}>
                    {user.first_name} {user.last_name}
                    {index < array.length - 1 ? ', ' : ''}
                  </React.Fragment>
                ))
              : t(StringKeys.NO_RESPONSE_TEAM_MEMBERS_ASSIGNED)}
          </Text>
        </View>

        {/* Row 3: Messages- Heading */}

        <View style={reportStyles.section}>
          <Text style={reportStyles.boldText}>{t(StringKeys.MESSAGES)}:</Text>
        </View>

        {/* Row 4: Emergency Messages */}

        {emergencyMessages.length > 0 && (
          <View style={reportStyles.messagesSection}>
            {emergencyMessages.map((msg, index) => (
              <Text key={index} style={reportStyles.emergencyMessageText}>
                {msg.sender_first_name} {msg.sender_last_name} -{' '}
                {new Date(msg.sent_timestamp).toLocaleTimeString()}:{' '}
                {msg.content
                  ? msg.content
                  : renderEmergencyMessageContent(
                      msg,
                      emergency.creator_first_name,
                      emergency.emergency_numbers || {
                        medical: '911',
                        fire: '911',
                        police: '911',
                      },
                      t,
                    )}
              </Text>
            ))}
          </View>
        )}

        {/* Row 5: User location history during emergency - Heading */}

        <View style={reportStyles.section}>
          <Text style={reportStyles.subsectionHeader}>
            {t(StringKeys.USERS_LOCATION_HISTORY_DURING_EMERGENCY)}
          </Text>
        </View>

        {/* Row 6: All User Locations */}

        {locationsByUserId &&
          Object.entries(locationsByUserId).map(([authId, locList]) => {
            const allUsers = [
              {
                auth_id: emergency.creator_auth_id,
                first_name: emergency.creator_first_name,
                last_name: emergency.creator_last_name,
              },
              ...(Array.isArray(emergency?.users) ? emergency.users : []),
            ];

            const user = allUsers.find((u) => u?.auth_id === authId);
            const fullName = user
              ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
              : `User: ${authId}`;

            return (
              <View key={authId} style={reportStyles.userLocationGroup}>
                <Text wrap={false} style={reportStyles.userNameHeader}>
                  {fullName}
                </Text>

                <View style={reportStyles.table}>
                  <View wrap={false} style={reportStyles.tableRow}>
                    <Text style={reportStyles.tableColHeader}>
                      {t(StringKeys.LOCATION)}
                    </Text>
                    <Text style={reportStyles.tableColHeader}>
                      {t(StringKeys.TIMESTAMP)}
                    </Text>
                  </View>

                  {Array.isArray(locList) && locList.length > 0 ? (
                    locList.map((loc, index) => (
                      <View
                        key={index}
                        wrap={false}
                        style={reportStyles.tableRow}
                      >
                        {loc.latitude != null && loc.longitude != null ? (
                          <Link
                            src={`https://www.google.com/maps?q=${Number(loc.latitude)},${Number(loc.longitude)}`}
                            style={reportStyles.tableCol}
                          >
                            {`${Number(loc.latitude).toFixed(5)}, ${Number(loc.longitude).toFixed(5)}`}
                          </Link>
                        ) : (
                          <Text style={reportStyles.tableCol}>
                            {t(StringKeys.LOCATION_NOT_AVAILABLE)}
                          </Text>
                        )}

                        <Text style={reportStyles.tableCol}>
                          {loc.creation_timestamp
                            ? new Date(
                                Number(loc.creation_timestamp),
                              ).toLocaleString()
                            : 'N/A'}
                        </Text>
                      </View>
                    ))
                  ) : (
                    <View style={reportStyles.tableRow}>
                      <Text style={reportStyles.tableCol}>
                        {t(StringKeys.LOCATION_NOT_AVAILABLE)}
                      </Text>
                      <Text style={reportStyles.tableCol}>—</Text>
                    </View>
                  )}
                </View>
              </View>
            );
          })}

        {/*Footer*/}
        <FooterText user={orgUser} t={t} />
      </Page>
    </Document>
  );
};
